import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import {
    ArrowClockwise,
    ArrowLeft,
    BuildingFill,
    CardChecklist,
    Diagram3Fill,
    FolderFill,
    GearFill,
    PeopleFill,
    PersonBadgeFill,
} from 'react-bootstrap-icons';
import { useNavigate, useParams } from 'react-router-dom';
import {
    fetchOrganizationOverview,
    OrganizationItem,
    OrganizationJob,
    OrganizationOverviewResponse,
    OrganizationProject,
    OrganizationTeam,
    OrganizationOverviewUser,
} from '../api/organizations';
import { jobTypeLabel, normalizeJobType } from '../utils/jobType';
import { projectJobsRoute } from '../utils/routes';
import './OrganizationDetail.css';

interface UnifiedOrganizationData
{
    organization: OrganizationItem;
    users: OrganizationUser[];
    teams: OrganizationTeam[];
    projects: OrganizationProject[];
    jobs: OrganizationJob[];
    llmConfig: { model?: string; temperature?: number };
    promptsCount: number;
}

interface OrganizationUser
{
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    is_admin: boolean;
    created_at?: string;
}

function splitFullName(fullName?: string | null): { firstName: string; lastName: string }
{
    const normalized = (fullName || '').trim();
    if (!normalized)
    {
        return { firstName: '', lastName: '' };
    }

    const parts = normalized.split(/\s+/);
    if (parts.length === 1)
    {
        return { firstName: parts[0], lastName: '' };
    }

    return {
        firstName: parts[0],
        lastName: parts.slice(1).join(' '),
    };
}

function mapOverviewUser(item: OrganizationOverviewUser): OrganizationUser
{
    const names = splitFullName(item.full_name);
    return {
        id: item.id,
        first_name: names.firstName,
        last_name: names.lastName,
        email: item.email,
        is_admin: (item.role || '').toLowerCase() === 'admin',
        created_at: item.created_at,
    };
}

function mapOverviewResponseToUnifiedData(overview: OrganizationOverviewResponse): UnifiedOrganizationData
{
    const users = (overview.users?.items || []).map(mapOverviewUser);
    const teams = overview.teams?.items || [];
    const projects = overview.projects?.items || [];
    const jobs = overview.jobs?.items || [];

    return {
        organization: overview,
        users,
        teams,
        projects,
        jobs,
        llmConfig: overview.llm_config || {},
        promptsCount: overview.prompts_count || 0,
    };
}

function buildLoadErrorMessage(err: any): string
{
    if (err?.response?.data?.detail)
    {
        return err.response.data.detail;
    }

    return 'Failed to load organization detail data.';
}

function formatDate(iso?: string | null): string
{
    if (!iso)
    {
        return '—';
    }

    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime()))
    {
        return '—';
    }

    return parsed.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function getStatusClass(status?: string): string
{
    const normalized = (status || '').toLowerCase();
    if (normalized === 'completed')
    {
        return 'od-badge od-badge-success';
    }
    if (normalized === 'failed' || normalized === 'error')
    {
        return 'od-badge od-badge-danger';
    }
    if (normalized === 'running' || normalized === 'pending')
    {
        return 'od-badge od-badge-warning';
    }
    return 'od-badge od-badge-neutral';
}

const OrganizationDetail: React.FC = () =>
{
    const { orgId } = useParams();
    const navigate = useNavigate();

    const [data, setData] = useState<UnifiedOrganizationData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadData = useCallback(async () =>
    {
        if (!orgId)
        {
            setError('Missing organization id in route.');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try
        {
            const orgNumericId = Number(orgId);
            const overview = await fetchOrganizationOverview(orgNumericId);
            setData(mapOverviewResponseToUnifiedData(overview));
        }
        catch (err: any)
        {
            setError(buildLoadErrorMessage(err));
        }
        finally
        {
            setLoading(false);
        }
    }, [orgId]);

    useEffect(() =>
    {
        loadData().catch(() => undefined);
    }, [loadData]);

    const metrics = useMemo(() =>
    {
        if (!data)
        {
            return {
                repositoriesLinked: 0,
                completedJobs: 0,
                activeJobs: 0,
                failedJobs: 0,
                adminUsers: 0,
            };
        }

        return {
            repositoriesLinked: data.projects.filter((project) => !!project.repository).length,
            completedJobs: data.jobs.filter((job) => (job.status || '').toLowerCase() === 'completed').length,
            activeJobs: data.jobs.filter((job) => ['running', 'pending'].includes((job.status || '').toLowerCase())).length,
            failedJobs: data.jobs.filter((job) => ['failed', 'error'].includes((job.status || '').toLowerCase())).length,
            adminUsers: data.users.filter((user) => !!user.is_admin).length,
        };
    }, [data]);

    const recentProjects = useMemo(() =>
    {
        if (!data)
        {
            return [];
        }

        return [...data.projects]
            .sort((a, b) => (b.created_at ? new Date(b.created_at).getTime() : 0) - (a.created_at ? new Date(a.created_at).getTime() : 0))
            .slice(0, 6);
    }, [data]);

    const recentJobs = useMemo(() =>
    {
        if (!data)
        {
            return [];
        }

        return [...data.jobs]
            .sort((a, b) => (b.created_at ? new Date(b.created_at).getTime() : 0) - (a.created_at ? new Date(a.created_at).getTime() : 0))
            .slice(0, 8);
    }, [data]);

    if (loading)
    {
        return (
            <div className="od-loading-state">
                <Spinner animation="border" size="sm" />
                <span>Loading organization details...</span>
            </div>
        );
    }

    if (error || !data)
    {
        return (
            <div className="od-page">
                <div className="od-toolbar">
                    <button className="od-btn od-btn-outline" onClick={() => navigate('/admin/organizations')} type="button">
                        <ArrowLeft size={14} />
                        Back to Organizations
                    </button>
                </div>
                <Alert variant="danger" className="mb-0">
                    {error || 'Organization details are unavailable.'}
                </Alert>
            </div>
        );
    }

    return (
        <div className="od-page">
            <div className="od-toolbar">
                <button className="od-btn od-btn-outline" onClick={() => navigate('/admin/organizations')} type="button">
                    <ArrowLeft size={14} />
                    Back to Organizations
                </button>
                <button className="od-btn od-btn-primary" onClick={() => loadData()} type="button">
                    <ArrowClockwise size={14} />
                    Refresh
                </button>
            </div>

            <section className="od-hero">
                <div className="od-hero-left">
                    <div className="od-hero-avatar">
                        <BuildingFill size={20} />
                    </div>
                    <div>
                        <h1 className="od-title">{data.organization.name}</h1>
                        <div className="od-meta-row">
                            <span>ID: {data.organization.id}</span>
                            <span>Type: {data.organization.organization_type || '—'}</span>
                            <span>Size: {data.organization.organization_size || '—'}</span>
                            <span>Created: {formatDate(data.organization.created_at)}</span>
                        </div>
                    </div>
                </div>
                <div className="od-hero-right">
                    <span className="od-badge od-badge-success">
                        Active
                    </span>
                </div>
            </section>

            <section className="od-grid od-grid-kpis">
                <article className="od-card">
                    <div className="od-card-title"><FolderFill size={16} /> Projects</div>
                    <div className="od-card-value">{data.projects.length}</div>
                    <div className="od-card-sub">{metrics.repositoriesLinked} linked repositories</div>
                </article>
                <article className="od-card">
                    <div className="od-card-title"><CardChecklist size={16} /> Jobs</div>
                    <div className="od-card-value">{data.jobs.length}</div>
                    <div className="od-card-sub">{metrics.completedJobs} completed, {metrics.failedJobs} failed</div>
                </article>
                <article className="od-card">
                    <div className="od-card-title"><PeopleFill size={16} /> Users</div>
                    <div className="od-card-value">{data.users.length}</div>
                    <div className="od-card-sub">{metrics.adminUsers} admins</div>
                </article>
                <article className="od-card">
                    <div className="od-card-title"><Diagram3Fill size={16} /> Teams</div>
                    <div className="od-card-value">{data.teams.length}</div>
                    <div className="od-card-sub">Structured delivery groups</div>
                </article>
                <article className="od-card">
                    <div className="od-card-title"><GearFill size={16} /> Configurations</div>
                    <div className="od-card-value">{data.promptsCount}</div>
                    <div className="od-card-sub">Prompts, model, temperature</div>
                </article>
            </section>

            <section className="od-grid od-grid-main">
                <article className="od-panel">
                    <div className="od-panel-header">
                        <h2>Projects</h2>
                        <button className="od-link-btn" type="button" onClick={() => navigate('/projects')}>Open Projects</button>
                    </div>
                    {recentProjects.length === 0 ? (
                        <div className="od-empty">No projects found for this organization.</div>
                    ) : (
                        <div className="od-list">
                            {recentProjects.map((project) => (
                                <div key={project.id} className="od-list-item">
                                    <div>
                                        <div className="od-item-title">{project.name}</div>
                                        <div className="od-item-sub">{project.project_type || 'simple'} • {project.repository ? 'Repository linked' : 'No repository'}</div>
                                    </div>
                                    <button className="od-btn od-btn-outline od-btn-sm" type="button" onClick={() => navigate(`/projects/${project.id}`)}>
                                        View
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </article>

                <article className="od-panel">
                    <div className="od-panel-header">
                        <h2>Jobs</h2>
                    </div>
                    {recentJobs.length === 0 ? (
                        <div className="od-empty">No jobs generated yet for this organization.</div>
                    ) : (
                        <div className="od-list">
                            {recentJobs.map((job) => (
                                <div key={job.id} className="od-list-item od-list-item-stacked">
                                    <div className="od-item-title">Job #{job.id} • {job.project_name || 'Unknown Project'}</div>
                                    <div className="od-item-sub">
                                        <span className={getStatusClass(job.status)}>{job.status || 'unknown'}</span>
                                        <span>{jobTypeLabel(normalizeJobType(job.job_type || job.generation_kind))}</span>
                                        <span>{formatDate(job.created_at)}</span>
                                    </div>
                                    {job.project_id && (
                                        <button
                                            className="od-btn od-btn-outline od-btn-sm"
                                            type="button"
                                            onClick={() => navigate(projectJobsRoute(job.project_id!, { jobId: job.id }))}
                                        >
                                            View in Project
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </article>
            </section>

            <section className="od-grid od-grid-main">
                <article className="od-panel">
                    <div className="od-panel-header">
                        <h2>Users</h2>
                        <button className="od-link-btn" type="button" onClick={() => navigate('/admin/users')}>Open Users</button>
                    </div>
                    {data.users.length === 0 ? (
                        <div className="od-empty">No users found in this organization.</div>
                    ) : (
                        <div className="od-list">
                            {data.users.slice(0, 8).map((user) => (
                                <div key={user.id} className="od-list-item">
                                    <div>
                                        <div className="od-item-title">{user.first_name} {user.last_name}</div>
                                        <div className="od-item-sub">{user.email}</div>
                                    </div>
                                    <span className={user.is_admin ? 'od-badge od-badge-primary' : 'od-badge od-badge-neutral'}>
                                        {user.is_admin ? 'Admin' : 'User'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </article>

                <article className="od-panel">
                    <div className="od-panel-header">
                        <h2>Teams</h2>
                        <button className="od-link-btn" type="button" onClick={() => navigate('/admin/users')}>Manage Teams</button>
                    </div>
                    {data.teams.length === 0 ? (
                        <div className="od-empty">No teams available for this organization.</div>
                    ) : (
                        <div className="od-list">
                            {data.teams.slice(0, 8).map((team) => (
                                <div key={team.id} className="od-list-item">
                                    <div>
                                        <div className="od-item-title">{team.name}</div>
                                        <div className="od-item-sub">{team.description || 'No description'}</div>
                                    </div>
                                    <span className="od-badge od-badge-neutral">{team.member_count ?? 0} members</span>
                                </div>
                            ))}
                        </div>
                    )}
                </article>
            </section>

            <section className="od-grid od-grid-main">
                <article className="od-panel">
                    <div className="od-panel-header">
                        <h2>Configuration</h2>
                        <button className="od-link-btn" type="button" onClick={() => navigate('/admin/config')}>Open Config</button>
                    </div>
                    <div className="od-config-grid">
                        <div className="od-config-item">
                            <div className="od-config-label">Model</div>
                            <div className="od-config-value">{data.llmConfig.model || 'Not configured'}</div>
                        </div>
                        <div className="od-config-item">
                            <div className="od-config-label">Temperature</div>
                            <div className="od-config-value">{typeof data.llmConfig.temperature === 'number' ? data.llmConfig.temperature : '—'}</div>
                        </div>
                        <div className="od-config-item">
                            <div className="od-config-label">Prompt Templates</div>
                            <div className="od-config-value">{data.promptsCount}</div>
                        </div>
                        <div className="od-config-item">
                            <div className="od-config-label">Test Execution</div>
                            <div className="od-config-value">{data.organization.feature_test_execution_enabled ? 'Enabled' : 'Disabled'}</div>
                        </div>
                        <div className="od-config-item">
                            <div className="od-config-label">Synthetic Data</div>
                            <div className="od-config-value">{data.organization.feature_synthetic_data_enabled ? 'Enabled' : 'Disabled'}</div>
                        </div>
                        <div className="od-config-item">
                            <div className="od-config-label">Exploratory Agent</div>
                            <div className="od-config-value">{data.organization.feature_exploratory_agent_enabled ? 'Enabled' : 'Disabled'}</div>
                        </div>
                        <div className="od-config-item">
                            <div className="od-config-label">Running Jobs</div>
                            <div className="od-config-value">{metrics.activeJobs}</div>
                        </div>
                    </div>
                </article>

                <article className="od-panel">
                    <div className="od-panel-header">
                        <h2>Health Snapshot</h2>
                    </div>
                    <div className="od-health-list">
                        <div className="od-health-item">
                            <PersonBadgeFill size={16} />
                            <span>{metrics.adminUsers} admins managing {data.users.length} users</span>
                        </div>
                        <div className="od-health-item">
                            <FolderFill size={16} />
                            <span>{metrics.repositoriesLinked} of {data.projects.length} projects have repositories attached</span>
                        </div>
                        <div className="od-health-item">
                            <CardChecklist size={16} />
                            <span>{metrics.completedJobs} completed jobs, {metrics.activeJobs} active jobs</span>
                        </div>
                        <div className="od-health-item">
                            <GearFill size={16} />
                            <span>{data.promptsCount} prompt templates configured for this organization context</span>
                        </div>
                    </div>
                </article>
            </section>
        </div>
    );
};

export default OrganizationDetail;
